import moment from 'moment';
import { Validation, ValidationRule } from '@vuelidate/core';
import { Ref } from 'vue';

// Define ExtendedValidationRule with specific properties and an index signature for validators
type ExtendedValidationRule = ValidationRule & {
  $invalid: boolean;
  $dirty: boolean;
  // Known validators are Validation objects
  required?: Validation<any, any>;
  numeric?: Validation<any, any>;
  minValue?: Validation<any, any>;
  maxValue?: Validation<any, any>;
  maxLength?: Validation<any, any>;
  url?: Validation<any, any>;
  [key: string]: Validation<any, any> | boolean | undefined; // Other validators via index signature
};

// Define PropertyPath type
type PropertyPath<T> = keyof T | ((v: Validation<any, T>) => ExtendedValidationRule);

export default class VuelidateUtil {
  // Validators
  public static todayOrFuture(dateString: string): boolean {
    if (dateString) {
      const enteredDate = moment.utc(dateString);
      const today = moment.utc().startOf('day');
      return enteredDate.isSameOrAfter(today);
    }
    return false;
  }

  public static objectName(name: string): boolean {
    return !!name.match(/^[A-Za-z0-9,\-_\(\)&\s]*$/);
  }

  public static intentName(name: string): boolean {
    return !!name.match(/^[A-Za-z0-9,\-_\(\)\s]*$/);
  }

  // Validator Settings
  public static getAllowedMaxLength<T>(
    vuelidate: Ref<Validation<any, T>>,
    propertyPath: PropertyPath<T>,
  ): number | undefined {
    const rule = VuelidateUtil.getValidationRule(vuelidate, propertyPath);
    return rule.maxLength?.$params.max;
  }

  // Checks
  public static isRequiredInvalid<T>(vuelidate: Ref<Validation<any, T>>, propertyPath: PropertyPath<T>): boolean {
    return VuelidateUtil.isValidationInvalid(vuelidate, propertyPath, 'required');
  }

  public static isMaxLengthInvalid<T>(vuelidate: Ref<Validation<any, T>>, propertyPath: PropertyPath<T>): boolean {
    return VuelidateUtil.isValidationInvalid(vuelidate, propertyPath, 'maxLength');
  }

  public static isValidationInvalid<T>(
    vuelidate: Ref<Validation<any, T>>,
    propertyPath: PropertyPath<T>,
    validationType?: string,
  ): boolean {
    if (!vuelidate.value) return false;
    const rule = VuelidateUtil.getValidationRule(vuelidate, propertyPath);
    if (!VuelidateUtil.isPropertyInvalidAndTouched(rule)) {
      return false;
    }

    return validationType ? ((rule[validationType] as Validation<any, any>)?.$invalid ?? false) : rule.$invalid;
  }

  public static hasPropertyBeenTouched<T>(vuelidate: Ref<Validation<any, T>>, propertyPath: PropertyPath<T>): boolean {
    const rule = VuelidateUtil.getValidationRule(vuelidate, propertyPath);
    return rule.$dirty;
  }

  public static getErrorMessage<T>(
    vuelidate: Ref<Validation<any, T>>,
    propertyPath: PropertyPath<T>,
    label: string,
    callback?: (validationRule: ExtendedValidationRule) => string | null,
  ): string | null {
    const rule = VuelidateUtil.getValidationRule(vuelidate, propertyPath);
    if (!rule || !rule.$invalid || !rule.$dirty) return null;

    if (rule.numeric?.$invalid) {
      return `${label} must be numeric`;
    }
    if (rule.required?.$invalid) {
      return `${label} is required`;
    }
    if (rule.minValue?.$invalid) {
      return `${label} must not be less than ${rule.minValue.$params.min}`;
    }
    if (rule.maxValue?.$invalid) {
      return `${label} must not be greater than ${rule.maxValue.$params.max}`;
    }
    if (rule.maxLength?.$invalid) {
      return `${label} must not be longer than ${rule.maxLength.$params.max} characters`;
    }

    if (rule.url?.$invalid) {
      return `${label} must be a valid URL`;
    }

    if (callback) {
      return callback(rule);
    }

    return null;
  }

  // Helper Methods
  private static getValidationRule<T>(
    vuelidate: Ref<Validation<any, T>>,
    propertyPath: PropertyPath<T>,
  ): ExtendedValidationRule {
    return (
      typeof propertyPath === 'function' ? propertyPath(vuelidate.value) : vuelidate.value[propertyPath]
    ) as ExtendedValidationRule;
  }

  private static isPropertyInvalidAndTouched(rule: ExtendedValidationRule): boolean {
    return rule.$invalid && rule.$dirty;
  }
}
